import React, { useEffect, useState } from "react";
import { Command } from "cmdk";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

export interface CommandAction {
  id: string;
  label: string;
  group: string;
  icon?: React.ComponentType<{ className?: string }>;
  keywords?: string;
  onSelect: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions: CommandAction[];
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange, actions }) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const groups = Array.from(new Set(actions.map((a) => a.group)));

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={() => onOpenChange(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -8 }}
            transition={{ duration: 0.15 }}
            className="fixed left-1/2 top-[12%] z-50 w-full max-w-xl -translate-x-1/2 px-4"
          >
            <Command
              className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-[#141416] shadow-2xl overflow-hidden"
              label="Paleta de comandos"
            >
              <div className="flex items-center gap-2 border-b border-zinc-100 dark:border-zinc-800 px-4">
                <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400 shrink-0" />
                <Command.Input
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Buscar secciones y acciones…"
                  className="flex-1 py-3.5 text-sm bg-transparent outline-none text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400"
                />
                <kbd className="hidden sm:inline text-[10px] text-zinc-400 border border-zinc-200 dark:border-zinc-700 rounded px-1.5 py-0.5">
                  ESC
                </kbd>
              </div>
              <Command.List className="max-h-80 overflow-y-auto p-2">
                <Command.Empty className="py-8 text-center text-sm text-zinc-500">
                  Sin resultados
                </Command.Empty>
                {groups.map((group) => (
                  <Command.Group
                    key={group}
                    heading={group}
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-zinc-500"
                  >
                    {actions
                      .filter((a) => a.group === group)
                      .map((action) => {
                        const Icon = action.icon;
                        return (
                          <Command.Item
                            key={action.id}
                            value={`${action.label} ${action.keywords || ""}`}
                            onSelect={() => {
                              action.onSelect();
                              onOpenChange(false);
                            }}
                            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-zinc-800 dark:text-zinc-200 cursor-pointer aria-selected:bg-zinc-100 dark:aria-selected:bg-zinc-800"
                          >
                            {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
                            {action.label}
                          </Command.Item>
                        );
                      })}
                  </Command.Group>
                ))}
              </Command.List>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
